import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { includes, isArray, isBoolean, isEmpty, isEqual } from "lodash";

import {Box, Button, Checkbox, Typography, TextField, InputAdornment} from "@mui/material";
import ResetIcon from "@mui/icons-material/RotateLeft";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import companyService from "@plannel/services/data/company-service";
import { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CustomDetailHeader from "../aggrid/CustomDetailHeader";
import appConfigService from "@plannel/services/system/app-config-service";
import CodeUtil from "@plannel/pages/utils/code-util";
import planConfigService from "@plannel/services/data/plan-config-service";
import { FS } from "@plannel/utils/font-util";

const DIALOG_TITLE = { SAVE: "SAVE", RESET: "RESET", CLOSE: "CLOSE" };

const AttributeConfigModal = (props) => {
  const { 
    t,
    viewName,
    open,
    setOpen,
    target,
    attrFields,
    setAttrColDefs,
    setHrchyColDefs,
    hrchyNumList,
    attrColsCollapsed,
    attrColumnGroupOptions = {},
    attrColumnOptions = {},
    hrchyColumnGroupOptions = {},
    hrchyColumnOptions = {},
    planConfig,
    setPlanConfig,
    // 비율범위 섹션을 함께 보여줄지(Item 전용). 예전 이름은 isTab 이었다 — 탭을 없애 의미가 바뀌었다
    hasRatioRange = false,
  } = props;

  const { snackBarMessageColor } = ColorUtil;
  const hrchyFields = hrchyNumList ? Object.keys(hrchyNumList) : [];
  const [isLoad, setIsLoad] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [attrData, setAttrData] = useState([]);   // 서버에서 온 값
  const [draft, setDraft] = useState([]);         // 편집 중인 값
  const [rawData, setRawData] = useState([]);

  const [max, setMax] = useState(planConfig?.objVal?.max ?? 100);
  const [min, setMin] = useState(planConfig?.objVal?.min ?? 0);

  const MIN = "min";
  const MAX = "max";

  const MODAL_TITLE = () => {
    const custom = "Customization";
    switch (target) {
      case "sales":
        return `feature${custom}`;
      case "date":
        return `feature${custom}`;
      default:
        return target + custom; 
    }
  }

  const isBasic = includes(["item", "customer"], target);
  
  const colField = isBasic ? "field" : "feature";
  const colFieldNm = isBasic ? "fieldName" : "featureName";
  // move to utils code
  const FEATURE_CONFIG_CD  = "FEATURE_ANALYSIS_FEATURE_NAME";
  
  const hrchyValueFormatter = (params) => {
    const { value } = params;
    if (!value) {
      return "";
    }
    
    if (typeof value === 'object') {
      return value?.name || "";
    }

    return value;
};

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  // obj response dat는 company에서 설정한 헤더 데이터.
  const setGridInfo = obj => {
    const gridData = [];
    const colDefs = [];
    const hrchyColDefs = [];

    // parent의 Column 속성을 다르게 주기위한 attrLoop 변수. (hrchy parent 속성이 필요하다면 hrchyLoop 변수 추가)
    let attrLoop = 0;

    // Target object는 attr column을 따로 지정한 경우가 있는 경우를 체크하는 것. (obj: item, customer)
    const targetObject = obj?.[target];
    const colWidth = isBasic ? 100 : 150;
    for (const attrField of attrFields) {
      let headerNameValue = targetObject?.[attrField];
      let isActive = false;
      if (isArray(targetObject)) {
        const targetObj = targetObject.filter(tar => isEqual(tar.feature.replace(/[^a-zA-Z0-9]/g, ''), attrField))?.[0];
         if (targetObj) {
          headerNameValue = targetObj.featureName;
          isActive = targetObj.activeFlg;
        };
      }
      let rowData = isBasic ? { field: attrField, fieldName: t(attrField) } : { feature: attrField, featureName: t(attrField), activeFlg: isActive };

      if (headerNameValue) {
        if (isBasic) {
          rowData = { field: attrField, fieldName: headerNameValue };
        } else {
          rowData = { feature: attrField, featureName: headerNameValue, activeFlg: isActive };
        }
      } else {
        headerNameValue = attrField;
      }
      gridData.push(rowData);

      if (isBoolean(attrColsCollapsed) && isEmpty(attrColumnGroupOptions)) {
        const parentDetailColumns = [...attrFields];
        // shift 하는 이유는 자기자신(parent)는 제외하기 위함(전체 attr 컬럼을 deep copy후 parent에 부여).
        parentDetailColumns.shift();

        const attrParentOption = {
          width: 120,
          headerComponent: CustomDetailHeader,
          headerComponentParams: {
            viewName,
            colsHide: attrColsCollapsed, reduxKey: "attrColsCollapsed",
            detailColumns: parentDetailColumns,
          },
        };
        // attrLoop: 0 = parent, child: other only hide option.
        if (attrLoop === 0) {
          colDefs.push({ headerName: headerNameValue, field: attrField, width: colWidth, ...attrParentOption, ...attrColumnOptions });
        } else {
          colDefs.push({ headerName: headerNameValue, field: attrField, width: colWidth, hide: attrColsCollapsed, ...attrColumnOptions });
        }
      } else {
        colDefs.push({ headerName: headerNameValue, field: attrField, width: colWidth, ...attrColumnGroupOptions, ...attrColumnOptions, hide: isBasic ? false : !isActive });
      }

      attrLoop++;
    }

    // hrchy column은 많지 않아 위 (collapsed 로직을 따로 설정하지 않음)
    for (const hrchyField of hrchyFields) {
      const hrchyBaseColDef = {
        cellEditor: "agRichSelectCellEditor", cellEditorPopup: true, comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
        valueFormatter: hrchyValueFormatter, width: 120
      };

      if (targetObject?.[hrchyField]) {
        const value = targetObject[hrchyField];
        const rowData = { field: hrchyField, fieldName: value };
        gridData.push(rowData);
        hrchyColDefs.push({ headerName: value, filterName: hrchyField, field: hrchyField, ...hrchyBaseColDef, ...hrchyColumnGroupOptions, ...hrchyColumnOptions });
      } else {
        gridData.push({ field: hrchyField, fieldName: t(hrchyField) });
        hrchyColDefs.push({ headerName: hrchyField, field: hrchyField, ...hrchyBaseColDef, ...hrchyColumnGroupOptions, ...hrchyColumnOptions });
      }
    }
    
    setAttrData(gridData);
    if (setAttrColDefs) {
      setAttrColDefs(colDefs);
    }

    if (setHrchyColDefs) {
      setHrchyColDefs(hrchyColDefs);
    }
  }

  const getCustomConfig = () => {
    const getConfigPromise = isBasic 
      ? companyService.getCustomConfig()
      :appConfigService.getAppConfigObjVal(FEATURE_CONFIG_CD);

    getConfigPromise
      .then(response => {
        setRawData(response.data);
        setGridInfo(response.data);
      })
      .catch(e => {
        debug(e);
      });
  }

  const saveCustomName = (obj) => {
    const saveData = { [target]: obj };
    return isBasic
      ? companyService.updateCustomConfig(saveData)
      : appConfigService.upsertAppConfigByCd({ saveData, configCd: FEATURE_CONFIG_CD });
  }

  /*
   * 이름과 비율범위는 저장 대상이 다르다(company/appConfig vs planConfig). 탭을 합쳐 Save 가 하나가 됐으니
   * **바뀐 것만 골라** 한 번에 보낸다.
   * 실패 시엔 모달을 닫지 않는다 — 닫으면 편집 내용이 사라진다.
   * (예전 이름 저장 경로는 실패해도 닫았다. 합치면서 둘 중 하나를 골라야 해 안전한 쪽으로 맞췄다.)
   */
  const saveAll = ({ customObj, saveName, saveRatio }) => {
    const jobs = [];
    if (saveName) {
      jobs.push(saveCustomName(customObj));
    }
    if (saveRatio) {
      jobs.push(saveRatioRange());
    }

    Promise.allSettled(jobs).then(results => {
      const rejected = results.filter(result => isEqual(result.status, "rejected"));
      if (rejected.length > 0) {
        rejected.forEach(result => debug(result.reason));
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        return;
      }

      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      getCustomConfig();
      setOpen(false);
    });
  }

  const getCustomObjParams = () => {
    if (isBasic) {
      const updateCustomObj = {};
      for (const data of draft) {
        if (!isEqual(t(data.field), data.fieldName)) {
          updateCustomObj[data.field] = data.fieldName;
        }
      }
      return updateCustomObj;
    } else {
      const updateCustomList = [];
      for (const data of draft) {
        const isDefaultName = isEqual(t(data.feature), data.featureName);
        if (isDefaultName && !data.activeFlg) {
          continue;
        }
        const feature = `${data?.feature.substring(0, 7)}_${data?.feature.substring(7, 9)}`;
        updateCustomList.push({
          feature,
          featureName: data?.featureName,
          activeFlg: data?.activeFlg
        });
      }
      return updateCustomList;
    }
  }

  // 비율범위(Item 전용 섹션)
  const initialSetting = () => {
    if (planConfig?.objVal) {
      setMin(planConfig.objVal?.min);
      setMax(planConfig.objVal?.max);
    } else {
      setMin(0);
      setMax(100);
    }
  }

  const saveRatioRange = () => {
    const configObj = planConfig?.objVal ?? {};

    configObj[MIN] = min;
    configObj[MAX] = max;

    const ratioConfigCd = target === "customer" ? "CUST_RATIO_RANGE" : "ITEM_RATIO_RANGE";
    const saveData = { ...planConfig, moduleCd: CodeUtil.module.DP, configCd: ratioConfigCd, activeFlg: true, objVal: configObj };
    return planConfigService.upsert([saveData])
      .then(response => {
        if (response.data) {
          setPlanConfig(response.data[0]);
        }
      });
  };

  // 저장값과 다른가 — initialSetting 의 기본값(0/100)과 같은 기준을 써야 한다
  const isRatioDirty = () => !isEqual(min, planConfig?.objVal?.min ?? 0) || !isEqual(max, planConfig?.objVal?.max ?? 100);

  const handleClose = () => {
    setGridInfo(rawData);
    initialSetting();
    setOpen(false);
  }

  const handleDialogOpen = action => {
    // 그리드 시절엔 DataState 가 "updated" 행을 들고 있었다 — 이제 draft 와 서버값(attrData)을 직접 비교한다
    const updatedDatas = draft.filter((row, index) => !isEqual(row, attrData[index]));
    const nameChanged = updatedDatas.length > 0;
    const ratioChanged = hasRatioRange && isRatioDirty();

    if (action === DIALOG_TITLE.SAVE) {
      if (hasRatioRange && min > max) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_ValueGreater")} ${min}` });
        return;
      }

      if (!nameChanged && !ratioChanged) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoSaveData") });
        return;
      }

      setDialogInfo({
        open: true, title: "save", content: t("MSG_SaveConfirm"),
        el: { customObj: nameChanged ? getCustomObjParams() : null, saveName: nameChanged, saveRatio: ratioChanged },
      });
    } else if (action === DIALOG_TITLE.RESET) {
      const resetMessage = isBasic ? "MSG_ResetAttrConfig" : "MSG_ResetFeatureConfig";
      // customObj: null 은 "이름 설정을 비운다"는 뜻이다(서버에 null 을 보내 초기화) — 비율범위는 건드리지 않는다
      setDialogInfo({
        open: true, title: "reset", severity: "info", content: t(resetMessage),
        el: { customObj: null, saveName: true, saveRatio: false },
      });
    } else if (action === DIALOG_TITLE.CLOSE) {
      if (nameChanged || ratioChanged) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        handleClose();
      }
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    switch (action) {
      case DIALOG_TITLE.SAVE:
      case DIALOG_TITLE.RESET: {
        saveAll(el);
        break;
      }
      case DIALOG_TITLE.CLOSE: {
        handleClose();
        break;
      }
      default: break;
    }
  }

  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal));
  }

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>%</InputAdornment> } : {};
  }

  // 열 때마다 서버값으로 되돌린다 — 취소하고 다시 열면 편집 흔적이 남으면 안 된다
  useEffect(() => {
    if (open) {
      setDraft(attrData.map(row => ({ ...row })));
    }
  }, [open, attrData]);

  // initial Load
  useEffect(() => {
    setIsLoad(true);
  }, []);

  useEffect(() => {
    if (isLoad){
      getCustomConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoad])
  
  useEffect(() => {
    if (planConfig?.objVal) {
      setMin(planConfig.objVal?.min);
      setMax(planConfig.objVal?.max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfig]);

  const changeRow = (index, key, value) => {
    setDraft(rows => rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  /*
   * 전체선택 — Chart Settings(MeasureConfigList)·Dimension Setting 의 active 헤더와 같은 규격.
   * activeFlg 를 가진 행만 대상으로 한다(계층 필드 행은 activeFlg 가 없다) — MeasureConfigList 가
   * hasActive 로 거르는 것과 같은 이유.
   */
  const activeRows = draft.filter(row => "activeFlg" in row);
  const allActive = activeRows.length > 0 && activeRows.every(row => Boolean(row.activeFlg));
  const someActive = activeRows.some(row => Boolean(row.activeFlg));
  const toggleAllActive = () => {
    setDraft(rows => rows.map(row => ("activeFlg" in row ? { ...row, activeFlg: !allActive } : row)));
  };

  // 열 제목 규격(Chart Settings 와 동일). 스크롤되는 목록에서는 sticky 를 얹어 헤더를 고정한다
  const headerSx = { fontSize: FS.BODY, color: "text.secondary" };
  // 섹션 제목 — DimensAttrModal 의 legendSx 와 같은 규격
  const sectionSx = { fontSize: FS.SUB, fontWeight: 600, color: "text.secondary", mb: 0.5 };
  const listHeaderSx = { ...headerSx, position: "sticky", top: 0, backgroundColor: "background.paper", zIndex: 1 };

  /*
   * 엘리먼트지 컴포넌트가 아니다 — 전에는 `const AttributeName = () => (...)` 로 부모 렌더 함수 안에서
   * 정의해 <AttributeName /> 로 썼다. 그러면 React 가 매 렌더마다 **새 컴포넌트 타입**으로 보고
   * 본문을 통째로 재마운트한다(편집 중 포커스가 날아간다).
   *
   * 본문은 "라벨 + 이름 입력"(+ 기본 대상이 아니면 활성 체크박스) 목록이다 — 정렬/리사이즈/메뉴를
   * 안 쓰므로 ag-grid 를 쓸 이유가 없었다. 행 수가 가변(속성 + 계층 필드)이라 30vh 에서 스크롤한다.
   */
  const attributeNameList = (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: isBasic ? "1fr 1fr" : "auto 1fr 1fr",
      gap: 1,
      alignItems: "center",
      maxHeight: "30vh",
      overflowY: "auto",
      my: 1,
    }}>
      {isBasic ? null : (
        <Box sx={{ ...listHeaderSx, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Checkbox
            size="small"
            checked={allActive}
            indeterminate={someActive && !allActive}
            onChange={toggleAllActive}
            sx={{ p: 0.25 }}
          />
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("activeFlg")}</Typography>
        </Box>
      )}
      <Typography sx={listHeaderSx}>{t(colField)}</Typography>
      <Typography sx={listHeaderSx}>{t(colFieldNm)}</Typography>
      {draft.map((row, index) => (
        <Box key={row[colField]} sx={{ display: "contents" }}>
          {isBasic ? null : (
            <Checkbox
              size="small"
              checked={Boolean(row.activeFlg)}
              onChange={e => changeRow(index, "activeFlg", e.target.checked)}
              sx={{ p: 0.25 }}
            />
          )}
          <Typography sx={{ fontSize: FS.TITLE, color: "text.primary" }}>{t(row[colField])}</Typography>
          <TextField
            size="small"
            value={row[colFieldNm] ?? ""}
            onChange={e => changeRow(index, colFieldNm, e.target.value)}
            sx={{ "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }}
          />
        </Box>
      ))}
    </Box>
  );

  return <>
    <BaseConfigModal
      open={open}
      onClose={() => handleDialogOpen(DIALOG_TITLE.CLOSE)}
      title={t(MODAL_TITLE())}
      onSave={() => handleDialogOpen(DIALOG_TITLE.SAVE)}
      extraActions={
        <Button size="small" startIcon={<ResetIcon />}
          onClick={() => handleDialogOpen(DIALOG_TITLE.RESET)}>
          {t("reset")}
        </Button>
      }
      size="lg">
      {/*
        * 탭을 없애고 한 본문에 쌓았다 — 비율범위(Item 전용)가 위, 이름 목록이 아래.
        * 섹션이 둘일 때만 제목을 단다(다른 화면은 이름 목록 하나뿐이라 제목이 군더더기).
        */}
      {hasRatioRange && (
        <>
          <Typography sx={sectionSx}>{t("ratioRange")}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "100px 100px", gap: 1, mb: 1 }}>
            <Typography sx={headerSx}>{t(MIN)}</Typography>
            <Typography sx={headerSx}>{t(MAX)}</Typography>
            <TextField
              size="small"
              name="min"
              key={"min"}
              InputProps={inputPropsByValue(min)}
              value={min}
              required
              error={checkValidation(min)}
              onChange={e => setMin(parseInt(e.target.value, 10))}
              sx={{ "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }}
            />
            <TextField
              size="small"
              name="max"
              key={"max"}
              InputProps={inputPropsByValue(max)}
              value={max}
              required
              error={checkValidation(max)}
              onChange={e => setMax(parseInt(e.target.value, 10))}
              sx={{ "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }}
            />
          </Box>
          <Typography sx={sectionSx}>{t("customName")}</Typography>
        </>
      )}
      {attributeNameList}
    </BaseConfigModal>
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
      el={dialogInfo.el}
    />
  </>
}
export default withTranslation()(AttributeConfigModal);